import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';

import { type Config, fetchConfig } from '@/lib/ab-testing';
import { ABProvider } from '@/lib/ab-testing/react';

const AB_API_BASE = 'https://app.userjourneys.ai';
const AB_CONFIG_CACHE_KEY = 'ab_config_cache';

function getCachedConfig(): Config | null {
  try {
    const raw = localStorage.getItem(AB_CONFIG_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setCachedConfig(config: Config) {
  try {
    localStorage.setItem(AB_CONFIG_CACHE_KEY, JSON.stringify(config));
  } catch {
    // localStorage full or unavailable — ignore
  }
}

export function ABConfigProvider({ children }: { children: React.ReactNode }) {
  const posthog = usePostHog();
  // Bootstrap from cache so the first render has a config immediately
  const [config, setConfig] = useState<Config | null>(() => getCachedConfig());
  const [configLoaded, setConfigLoaded] = useState(() => getCachedConfig() !== null);

  // Always bucket by PostHog distinct_id. It's stable from first page load
  // and survives the anonymous → authenticated transition on the same device.
  // PostHog's identify() merges it with the auth userId for analytics attribution.
  const bucketingId = (posthog?.get_distinct_id?.() as string | undefined) ?? null;

  useEffect(() => {
    let isMounted = true;

    const loadConfig = async () => {
      const projectKey = import.meta.env.VITE_UJ_PROJECT_KEY as string | undefined;
      if (!projectKey) {
        if (isMounted) setConfigLoaded(true);
        return;
      }

      const nextConfig = await fetchConfig({
        projectKey,
        apiBase: AB_API_BASE,
      });

      if (isMounted) {
        if (nextConfig) {
          setConfig(nextConfig);
          setCachedConfig(nextConfig);
        }
        setConfigLoaded(true);
      }
    };

    void loadConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <ABProvider
      config={config}
      configLoaded={configLoaded}
      posthog={posthog}
      userId={bucketingId}>
      {children}
    </ABProvider>
  );
}
